import { useStore } from "@tanstack/react-form";
import React from "react";

import {
  isCaseTypeRequiredForOrg,
  parseCaseTypeRequiredOrgIds,
} from "@blis/shared/schemas/settings";

import { StatIndicator } from "@/components/Indicators";
import { DatePickerInput } from "@/components/ui/date-picker";
import {
  DatePickerTimeZone,
  DatePickerWithZone,
} from "@/components/ui/date-picker-with-zone";
import { FieldError, Input, TextField } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { useSettings } from "@/features/admin/hooks";
import { parseZonedDateTime } from "../detail/SampleSection";
import { useAccessionForm } from "./AccessionFormProvider";
import { CaseTypeSelect } from "./components/CaseTypeSelect";
import { FormField } from "./layout";

export function AdditionalSection() {
  return (
    <div className="border rounded-md bg-background">
      <ShippingFields />
    </div>
  );
}

function CaseTypeField() {
  const { form } = useAccessionForm();
  const { settings } = useSettings();
  const orgId = useStore(form.store, (s) => s.values.orgId);
  const caseTypeMeta = useStore(form.store, (s) => s.fieldMeta.caseType);
  const requiredOrgIds = parseCaseTypeRequiredOrgIds(
    settings.caseTypeRequiredOrgIds,
  );
  const required = isCaseTypeRequiredForOrg(orgId, requiredOrgIds);

  React.useEffect(() => {
    if (!required) {
      form.setFieldValue("caseType", undefined);
    }
  }, [required, form]);

  if (!required) {
    return null;
  }

  return (
    <form.Field
      name="caseType"
      validators={{
        onSubmit: ({ value }) => (value ? undefined : "Case type is required"),
      }}
    >
      {(field) => {
        const error =
          field.state.meta.errors.join(", ") ||
          caseTypeMeta?.errors?.join(", ");
        return (
          <FormField
            title="Case type"
            description="Required for this client"
            error={error}
          >
            <CaseTypeSelect
              value={field.state.value}
              onChange={field.handleChange}
              isInvalid={!!error}
            />
          </FormField>
        );
      }}
    </form.Field>
  );
}

function ShippingFields() {
  const { form } = useAccessionForm();
  return (
    <div className="flex flex-col p-4 gap-4">
      <CaseTypeField />
      <form.Field name="packedAt">
        {(field) => (
          <FormField
            title="Packing time"
            description="The date and time the samples were packaged"
          >
            <DatePickerWithZone
              aria-label="Packing time"
              className="flex-row items-center gap-1 min-w-48"
              granularity="minute"
              hideTimeZone
              validationBehavior="aria"
              isInvalid={!!field.state.meta.errors.length}
              value={
                field.state.value
                  ? parseZonedDateTime(
                      field.state.value,
                      form.getFieldValue("packedTimezone"),
                    )
                  : null
              }
              onBlur={field.handleBlur}
              onChange={(val) => {
                if (!val) {
                  field.handleChange(null);
                  form.setFieldValue("packedTimezone", null);
                } else {
                  field.handleChange(val.toAbsoluteString());
                  form.setFieldValue("packedTimezone", val.timeZone);
                }
              }}
            >
              <DatePickerInput />
              <DatePickerTimeZone />
            </DatePickerWithZone>
          </FormField>
        )}
      </form.Field>

      <form.Field name="trackingId">
        {(field) => (
          <FormField
            title="Tracking #"
            description="Courier tracking number, if available"
            htmlFor="trackingId"
          >
            <TextField
              type="text"
              id="trackingId"
              aria-label="Tracking #"
              value={field.state.value}
              onChange={(val) => field.handleChange(val)}
              onBlur={field.handleBlur}
              isInvalid={!!field.state.meta.errors.length}
              validationBehavior="aria"
            >
              <Input placeholder="Tracking ID" />
              <FieldError>{field.state.meta.errors.join(", ")}</FieldError>
            </TextField>
          </FormField>
        )}
      </form.Field>

      <form.Field name="requisitionId">
        {(field) => (
          <FormField
            title="Requisition ID"
            description="Optional requisition identifier"
            htmlFor="requisitionId"
          >
            <TextField
              type="text"
              id="requisitionId"
              aria-label="Requisition ID"
              value={field.state.value}
              onChange={(val) => field.handleChange(val)}
              onBlur={field.handleBlur}
              isInvalid={!!field.state.meta.errors.length}
              validationBehavior="aria"
            >
              <Input
                placeholder="Requisition ID"
                onKeyDown={(e) => {
                  // Prevent form submission when Enter is pressed in this field
                  // This handles scanner input that typically sends Enter after scanning
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              />
              <FieldError>{field.state.meta.errors.join(", ")}</FieldError>
            </TextField>
          </FormField>
        )}
      </form.Field>

      <form.Field name="priority">
        {(field) => (
          <FormField
            title={
              <div className="flex items-center gap-1">
                STAT <StatIndicator />
              </div>
            }
            description="Flag this accession as STAT priority"
            htmlFor="priority"
          >
            <Switch
              isSelected={field.state.value === "STAT"}
              onChange={(flag) => {
                field.handleChange(flag ? "STAT" : "ROUTINE");
              }}
            />
          </FormField>
        )}
      </form.Field>
    </div>
  );
}
